import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { gestorVisivel } from '../lib/filtroGestor'

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']
const ANO_ATUAL = new Date().getFullYear()
const MES_ATUAL = new Date().getMonth() + 1
const MES_PADRAO = MES_ATUAL === 1 ? 12 : MES_ATUAL - 1
const ANO_PADRAO = MES_ATUAL === 1 ? ANO_ATUAL - 1 : ANO_ATUAL

// Estado de cada card de destaque quando nao ha' indicador do lado dele: o
// positivo com tudo NOK, o negativo com tudo no alvo. Cada um assume a cor da
// noticia que da', nao a do proprio card.
const SEM_DESTAQUE_POSITIVO = {
  texto: 'Nenhuma meta atingida.', emoji: '⚠️',
  cor: 'var(--red)', bg: 'var(--red-bg)', border: 'var(--red-border)',
}
const SEM_DESTAQUE_NEGATIVO = {
  texto: 'Todas as metas atingidas!', emoji: '✅',
  cor: 'var(--green)', bg: 'var(--green-bg)', border: 'var(--green-border)',
}

export default function Home() {
  const { isAdmin, isGestor, profile, isSuperAdmin, selectedCompany, filtroGestor, setFiltroGestor } = useAuth()
  const navigate = useNavigate()
  const [indicadores, setIndicadores] = useState([])
  const [fcasAbertas, setFcasAbertas] = useState([])
  const [loading, setLoading] = useState(true)
  const [mesRef, setMesRef] = useState(MES_PADRAO)
  const [anoRef, setAnoRef] = useState(ANO_PADRAO)

  useEffect(() => { fetchStats(mesRef, anoRef) }, [mesRef, anoRef, selectedCompany])

  async function fetchStats(mes, ano) {
    setLoading(true)
    let indsQ = supabase.from('indicadores').select('id,nome,objetivo,unidade,gestor_id,apenas_tatico,profiles(id,nome)')
    if (isSuperAdmin && selectedCompany) indsQ = indsQ.eq('company_id', selectedCompany.id)
    const [{ data: inds }, { data: res }, { data: fca }] = await Promise.all([
      indsQ,
      supabase.from('resultados').select('*').eq('ano', ano).eq('mes', mes),
      supabase.from('fcas').select('id,indicador_id,status').neq('status', 'Concluído')
    ])

    setFcasAbertas(fca || [])
    setIndicadores((inds || []).map(ind => {
      const r = res?.find(x => x.indicador_id === ind.id)
      return { ...ind, resultado_mes: r }
    }))
    setLoading(false)
  }

  const gestores = [...new Map(
    indicadores.filter(i => i.profiles?.id).map(i => [i.profiles.id, i.profiles])
  ).values()].sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'))

  // Um gestor filtra os coordenadores dele, nunca a si mesmo.
  const isAdminOrSuper = isAdmin || isSuperAdmin
  const opcoesGestor = isAdminOrSuper ? gestores : gestores.filter(g => g.id !== profile?.id)
  // Sem seletor visivel, o filtro compartilhado nao vale nesta tela — senao a
  // pessoa veria a tela recortada sem nada na interface explicando o porque.
  const podeFiltrarGestor = isAdminOrSuper || (isGestor && opcoesGestor.length > 0)
  const gestorSel = podeFiltrarGestor ? gestorVisivel(filtroGestor, opcoesGestor) : ''

  // Recorte por gestor — inclui taticos, usado so' para o card "FCAs abertos",
  // porque o fluxo de FCA nao muda para indicadores taticos.
  const doGestor = gestorSel
    ? indicadores.filter(i => i.profiles?.id === gestorSel)
    : indicadores

  // Numeros de desempenho (contagem, OK/NOK, aderencia, destaques, trofeu)
  // consideram apenas indicadores estrategicos.
  const indicadoresFiltrados = doGestor.filter(i => !i.apenas_tatico)

  const indIdsFiltrados = new Set(doGestor.map(i => i.id))

  let ok = 0, nok = 0
  indicadoresFiltrados.forEach(ind => {
    const r = ind.resultado_mes
    if (r != null && r.resultado != null && r.meta != null) {
      const isOk = ind.objetivo === 'maior' ? r.resultado >= r.meta : r.resultado <= r.meta
      if (isOk) ok++; else nok++
    }
  })
  const fcasCount = fcasAbertas.filter(f => indIdsFiltrados.has(f.indicador_id)).length
  const totalComDados = ok + nok
  const aderencia = totalComDados > 0 ? Math.round((ok / totalComDados) * 100) : null
  const aderenciaColor = aderencia === null ? 'var(--text2)' : aderencia >= 80 ? 'var(--green)' : aderencia >= 50 ? 'var(--amber)' : 'var(--red)'

  const mesPerfeitoGestor = gestorSel && aderencia === 100 && totalComDados > 0 && totalComDados === indicadoresFiltrados.length
    ? gestores.find(g => g.id === gestorSel)
    : null

  const indicadoresComDesvio = indicadoresFiltrados
    .filter(ind => {
      const r = ind.resultado_mes
      return r != null && r.resultado != null && r.meta != null && r.meta !== 0
      // meta !== 0 mantido aqui pois desvio % não faz sentido com meta zerada
    })
    .map(ind => {
      const r = ind.resultado_mes
      const desvio = ind.objetivo === 'maior'
        ? ((r.resultado - r.meta) / r.meta) * 100
        : ((r.meta - r.resultado) / r.meta) * 100
      return { ...ind, desvio }
    })

  // desvio >= 0 e' exatamente a regra de OK: para 'maior', resultado >= meta;
  // para 'menor', resultado <= meta. Cada destaque so' olha o proprio lado —
  // sem isso o positivo elegia o menos pior de um conjunto todo NOK e o
  // mostrava em verde com ▲, como se a meta tivesse sido batida.
  const positivos = indicadoresComDesvio.filter(ind => ind.desvio >= 0)
  const destaquePositivo = positivos.length > 0
    ? positivos.reduce((a, b) => a.desvio > b.desvio ? a : b)
    : null

  const negativos = indicadoresComDesvio.filter(ind => ind.desvio < 0)
  const destaqueNegativo = negativos.length > 0
    ? negativos.reduce((a, b) => a.desvio < b.desvio ? a : b)
    : null

  const todosNoAlvo = indicadoresComDesvio.length > 0 && negativos.length === 0
  const nenhumNoAlvo = indicadoresComDesvio.length > 0 && positivos.length === 0

  const metricas = [
    { label: 'Indicadores', val: indicadoresFiltrados.length, color: 'var(--text)' },
    { label: `OK em ${MESES[mesRef - 1]}`, val: ok, color: 'var(--green)' },
    { label: `NOK em ${MESES[mesRef - 1]}`, val: nok, color: nok > 0 ? 'var(--red)' : 'var(--text)' },
    { label: 'FCAs abertos', val: fcasCount, color: fcasCount > 0 ? 'var(--amber)' : 'var(--green)' },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>Olá, {profile?.nome?.split(' ')[0]} 👋</h2>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>
            Resumo do mês de {MESES[mesRef - 1]} {anoRef}
            {(isAdmin || isSuperAdmin) && <span className="badge badge-blue" style={{ marginLeft: 8 }}>{isSuperAdmin ? 'Super Admin — visão geral' : 'Admin — visão geral'}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={mesRef} onChange={e => setMesRef(Number(e.target.value))} style={{ width: 'auto' }}>
            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={anoRef} onChange={e => setAnoRef(Number(e.target.value))} style={{ width: 'auto' }}>
            {[ANO_ATUAL - 2, ANO_ATUAL - 1, ANO_ATUAL].map(a => <option key={a}>{a}</option>)}
          </select>
          {podeFiltrarGestor && (
            <select value={gestorSel} onChange={e => setFiltroGestor(e.target.value)} style={{ width: 'auto', minWidth: 180 }}>
              <option value="">{isAdminOrSuper ? 'Todos os gestores' : 'Todos os coordenadores'}</option>
              {opcoesGestor.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Troféu Mês Perfeito */}
      {!loading && mesPerfeitoGestor && (
        <div className="trofeu-banner">
          <span className="trophy-icon">🏆</span>
          <div style={{ flex: 1, color: '#fff' }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85, marginBottom: 2 }}>
              Troféu Mês Perfeito
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
              {mesPerfeitoGestor.nome} bateu todas as {totalComDados} metas de {MESES[mesRef - 1]}/{anoRef}!
            </div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
              100% de aderência — resultado extraordinário 🎯
            </div>
          </div>
          <span style={{ fontSize: 36, opacity: 0.6 }}>🎯</span>
        </div>
      )}

      {/* Métricas */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
        <div style={{
          gridColumn: '1 / -1',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 10, padding: '16px 24px', boxShadow: 'var(--shadow)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 2 }}>Aderência às Metas — {MESES[mesRef - 1]}/{anoRef}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {loading ? '' : totalComDados > 0 ? `${ok} de ${totalComDados} indicadores com dados atingiram a meta` : 'Sem dados suficientes para calcular'}
            </div>
          </div>
          <div style={{ fontSize: 48, fontWeight: 700, color: loading ? 'var(--text3)' : aderenciaColor, lineHeight: 1 }}>
            {loading ? '—' : aderencia !== null ? `${aderencia}%` : '—'}
          </div>
        </div>
        {metricas.map(m => (
          <div key={m.label} style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '16px 20px', boxShadow: 'var(--shadow)'
          }}>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 28, fontWeight: 600, color: m.color }}>{loading ? '—' : m.val}</div>
          </div>
        ))}

        {/* Destaques */}
        {[
          { titulo: 'Destaque Positivo', emoji: '▲', ind: destaquePositivo, cor: 'var(--green)', corBorder: 'var(--green-border)', corBg: 'var(--green-bg)',
            vazio: nenhumNoAlvo ? SEM_DESTAQUE_POSITIVO : null },
          { titulo: 'Destaque Negativo', emoji: '▼', ind: destaqueNegativo, cor: 'var(--red)', corBorder: 'var(--red-border)', corBg: 'var(--red-bg)',
            vazio: todosNoAlvo ? SEM_DESTAQUE_NEGATIVO : null },
        ].map(({ titulo, emoji, ind, cor, corBorder, corBg, vazio }) => {
          return (
          <div key={titulo} style={{
            gridColumn: 'span 2',
            background: loading || (!ind && !vazio) ? 'var(--surface)' : vazio ? vazio.bg : corBg,
            border: `1px solid ${loading || (!ind && !vazio) ? 'var(--border)' : vazio ? vazio.border : corBorder}`,
            borderRadius: 10, padding: '16px 20px', boxShadow: 'var(--shadow)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>{titulo} — {MESES[mesRef - 1]}/{anoRef}</div>
              {loading ? (
                <div style={{ fontSize: 14, color: 'var(--text3)' }}>—</div>
              ) : vazio ? (
                <div style={{ fontWeight: 600, fontSize: 14, color: vazio.cor }}>{vazio.texto}</div>
              ) : !ind ? (
                <div style={{ fontSize: 13, color: 'var(--text3)' }}>Sem dados suficientes</div>
              ) : (
                <>
                  <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ind.nome}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                    Meta: {Number(ind.resultado_mes.meta).toFixed(2)} · Resultado: {Number(ind.resultado_mes.resultado).toFixed(2)}
                  </div>
                </>
              )}
            </div>
            {!loading && vazio ? (
              <div style={{ fontSize: 32, lineHeight: 1, marginLeft: 16, flexShrink: 0 }}>{vazio.emoji}</div>
            ) : !loading && ind && (
              <div style={{ fontSize: 32, fontWeight: 700, color: cor, lineHeight: 1, marginLeft: 16, flexShrink: 0 }}>
                {emoji} {Math.abs(ind.desvio).toFixed(1)}%
              </div>
            )}
          </div>
          )
        })}
      </div>

      {/* Tabela de indicadores */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Indicadores — {MESES[mesRef - 1]}/{anoRef}</h3>
          <button className="btn btn-sm" onClick={() => navigate('/indicadores')}>Ver todos</button>
        </div>
        {loading ? (
          <div className="empty-state">Carregando...</div>
        ) : indicadoresFiltrados.length === 0 ? (
          <div className="empty-state">
            {indicadores.length === 0
              ? 'Nenhum indicador cadastrado ainda.'
              : doGestor.length > 0
                ? 'Nenhum indicador estratégico neste recorte — os táticos ficam na tela de Indicadores.'
                : 'Nenhum indicador para este responsável.'}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Indicador</th>
                  {(isAdmin || isSuperAdmin || isGestor) && <th>Responsável</th>}
                  <th>Meta</th>
                  <th>Resultado</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {indicadoresFiltrados.map(ind => {
                  const r = ind.resultado_mes
                  const ok = r != null && r.resultado != null && r.meta != null
                    ? (ind.objetivo === 'maior' ? r.resultado >= r.meta : r.resultado <= r.meta)
                    : null
                  return (
                    <tr key={ind.id}>
                      <td>
                        <span style={{ fontWeight: 500 }}>{ind.nome}</span>
                        {ind.unidade && <span className="badge badge-gray" style={{ marginLeft: 6 }}>{ind.unidade}</span>}
                      </td>
                      {(isAdmin || isSuperAdmin || isGestor) && <td style={{ color: 'var(--text2)' }}>{ind.profiles?.nome}</td>}
                      <td style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
                        {r?.meta != null ? r.meta.toFixed(2) : '—'}
                      </td>
                      <td style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
                        {r?.resultado != null ? r.resultado.toFixed(2) : '—'}
                      </td>
                      <td>
                        {ok === null
                          ? <span className="badge badge-gray">—</span>
                          : ok
                            ? <span className="badge badge-green" style={{ minWidth: 52 }}>OK</span>
                            : <span className="badge badge-red" style={{ minWidth: 52 }}>NOK</span>}
                      </td>
                      <td>
                        <button className="btn btn-sm btn-ghost"
                          onClick={() => navigate(`/dashboard/${ind.id}`)}>
                          Gráfico →
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
